import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import companyService from '../../services/companyService';

// ── Animations ──

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to { opacity: 1; transform: translateY(0); }
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

// ── Layout ──

const PageWrapper = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 48px 16px;
  background: #0a0a14;
`;

const Card = styled.div`
  width: 100%;
  max-width: 480px;
  background: #10101c;
  border: 1px solid #2a2a44;
  border-radius: 16px;
  padding: 40px 32px;
  animation: ${fadeIn} 0.5s ease-out;

  @media (max-width: 520px) {
    padding: 32px 20px;
  }
`;

const HeaderBar = styled.div`
  height: 3px;
  width: 48px;
  background: linear-gradient(90deg, #6366F1, #8B5CF6, #EC4899);
  border-radius: 2px;
  margin-bottom: 24px;
`;

const Title = styled.h1`
  font-size: 1.5rem;
  font-weight: 700;
  color: #e0e0ec;
  margin: 0 0 8px 0;
`;

const Description = styled.p`
  font-size: 0.88rem;
  color: #8888a8;
  line-height: 1.6;
  margin: 0 0 32px 0;
`;

// ── Form Elements ──

const FormGroup = styled.div`
  margin-bottom: 24px;
`;

const Label = styled.label`
  display: block;
  font-size: 0.82rem;
  font-weight: 600;
  color: #e0e0ec;
  margin-bottom: 8px;
`;

const RequiredMark = styled.span`
  color: #EC4899;
  margin-left: 4px;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 16px;
  background: #181830;
  border: 1px solid #2a2a44;
  border-radius: 10px;
  color: #e0e0ec;
  font-size: 0.92rem;
  outline: none;
  transition: border-color 0.2s;
  box-sizing: border-box;

  &::placeholder {
    color: #555570;
  }

  &:focus {
    border-color: #6366F1;
  }
`;

const HelperText = styled.p`
  font-size: 0.76rem;
  color: #8888a8;
  margin: 8px 0 0 0;
  line-height: 1.5;
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: 14px 24px;
  background: linear-gradient(135deg, #6366F1, #8B5CF6);
  color: #fff;
  border: none;
  border-radius: 10px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.2s, transform 0.1s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:hover:not(:disabled) {
    opacity: 0.9;
    transform: translateY(-1px);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Spinner = styled.div`
  width: 18px;
  height: 18px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: #fff;
  border-radius: 50%;
  animation: ${spin} 0.6s linear infinite;
`;

const ErrorMessage = styled.div`
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.3);
  border-radius: 10px;
  padding: 12px 16px;
  margin-bottom: 24px;
  font-size: 0.84rem;
  color: #f87171;
  line-height: 1.5;
`;

// ── Unauthenticated State ──

const AuthPrompt = styled.div`
  text-align: center;
`;

const AuthMessage = styled.p`
  font-size: 0.9rem;
  color: #8888a8;
  margin: 0 0 24px 0;
  line-height: 1.6;
`;

const LoginButton = styled.button`
  padding: 12px 32px;
  background: #6366F1;
  color: #fff;
  border: none;
  border-radius: 10px;
  font-size: 0.92rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: #8B5CF6;
  }
`;

// ── Component ──

const CompanyCreatePage = () => {
  const { t } = useLanguage();
  const { isAuthenticated, login } = useAuth();
  const navigate = useNavigate();

  const [name, setName] = useState('');
  const [departments, setDepartments] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    const trimmedName = name.trim();
    if (!trimmedName) {
      setError(t('組織名を入力してください。', 'Please enter an organization name.'));
      return;
    }

    const deptList = departments
      .split(',')
      .map((d) => d.trim())
      .filter((d) => d.length > 0);

    setLoading(true);
    try {
      const result = await companyService.createCompany(trimmedName, deptList);
      navigate(`/company/${result.slug}`);
    } catch (err) {
      setError(
        err.message ||
          t(
            '組織の作成に失敗しました。しばらくしてからお試しください。',
            'Failed to create organization. Please try again later.'
          )
      );
    } finally {
      setLoading(false);
    }
  };

  // ── Unauthenticated View ──

  if (!isAuthenticated) {
    return (
      <PageWrapper>
        <Card>
          <HeaderBar />
          <Title>{t('組織を作成', 'Create Organization')}</Title>
          <AuthPrompt>
            <AuthMessage>
              {t(
                '組織を作成するにはログインが必要です。',
                'Please sign in to create an organization.'
              )}
            </AuthMessage>
            <LoginButton onClick={login}>
              {t('ログイン', 'Sign In')}
            </LoginButton>
          </AuthPrompt>
        </Card>
      </PageWrapper>
    );
  }

  // ── Authenticated View ──

  return (
    <PageWrapper>
      <Card>
        <HeaderBar />
        <Title>{t('組織を作成', 'Create Organization')}</Title>
        <Description>
          {t(
            'STRUCT CODEの組織分析を利用するための組織を作成します。メンバーを招待して、チームの構造特性を可視化できます。',
            'Create an organization to use STRUCT CODE team analytics. Invite members and visualize your team\'s structural characteristics.'
          )}
        </Description>

        {error && <ErrorMessage>{error}</ErrorMessage>}

        <form onSubmit={handleSubmit}>
          <FormGroup>
            <Label>
              {t('組織名', 'Organization Name')}
              <RequiredMark>*</RequiredMark>
            </Label>
            <Input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('例: 株式会社サンプル', 'e.g. Acme Corporation')}
              disabled={loading}
              autoFocus
            />
          </FormGroup>

          <FormGroup>
            <Label>{t('初期部署', 'Initial Departments')}</Label>
            <Input
              type="text"
              value={departments}
              onChange={(e) => setDepartments(e.target.value)}
              placeholder={t(
                '例: 営業部, 開発部, 人事部',
                'e.g. Sales, Engineering, HR'
              )}
              disabled={loading}
            />
            <HelperText>
              {t(
                'カンマ区切りで複数の部署を指定できます。後から追加・変更も可能です。',
                'Separate multiple departments with commas. You can add or change them later.'
              )}
            </HelperText>
          </FormGroup>

          <SubmitButton type="submit" disabled={loading || !name.trim()}>
            {loading ? (
              <>
                <Spinner />
                {t('作成中...', 'Creating...')}
              </>
            ) : (
              t('組織を作成する', 'Create Organization')
            )}
          </SubmitButton>
        </form>
      </Card>
    </PageWrapper>
  );
};

export default CompanyCreatePage;
